import React, { useRef, useEffect } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFlag } from "@fortawesome/free-solid-svg-icons";

export const ReplyForm = ({
  onSubmit,
  text,
  setText,
  error,
  autoFocus = false,
}) => {
  const { t } = useTranslation();
  const inputRef = useRef(null);

  useEffect(() => {
    if (autoFocus && inputRef.current) {
      inputRef.current.focus();
    }
  }, [autoFocus]);

  return (
    <form onSubmit={onSubmit} className="mt-1 flex flex-col gap-1">
      <div className="flex items-center gap-2">
        <input
          ref={inputRef}
          type="text"
          placeholder={t("discussion.reply_placeholder")}
          value={text}
          onChange={(e) => setText(e.target.value)}
          className="flex-grow bg-white border border-gray-300 rounded-md px-3 py-1.5 text-sm dark:bg-gray-800 dark:border-gray-700 dark:text-gray-300"
        />
        <button
          type="submit"
          className="bg-gradient-to-r from-primary-600 to-primary-700 text-white px-4 py-1.5 rounded text-sm hover:from-primary-700 hover:to-primary-800 active:from-primary-800 active:to-primary-900 transition-all duration-300 dark:bg-primary-600 dark:hover:bg-primary-700 dark:active:bg-primary-800 dark:active:to-primary-900"
        >
          {t("discussion.post")}
        </button>
      </div>
      {error && (
        <p className="text-red-500 text-xs dark:text-red-400">{error}</p>
      )}
    </form>
  );
};

export const EditForm = ({ text, setText, onSave, onCancel }) => {
  const { t } = useTranslation();
  return (
    <div className="mt-1">
      <input
        type="text"
        value={text}
        onChange={(e) => setText(e.target.value)}
        className="w-full p-2 border rounded"
      />
      <div className="mt-1 space-x-2">
        <button
          onClick={onSave}
          className="text-xs bg-blue-500 text-white px-2 py-1 rounded dark:bg-primary-600 dark:text-white dark:hover:bg-primary-700"
        >
          {t("actions.save")}
        </button>
        <button
          onClick={onCancel}
          className="text-xs bg-gray-500 text-white px-2 py-1 rounded dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600"
        >
          {t("actions.cancel")}
        </button>
      </div>
    </div>
  );
};

export const ReportModal = ({ onClose, onSubmit, reason, setReason }) => {
  const { t } = useTranslation();
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div
        className="bg-white p-4 rounded-lg max-w-md w-full dark:bg-gray-800"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="text-lg font-bold mb-4 dark:text-gray-100">
          {t("discussion.report_modal.title")}
        </h3>
        <form onSubmit={onSubmit}>
          <input
            type="text"
            placeholder={t("discussion.report_modal.placeholder")}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            className="w-full p-2 mb-4 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-800 dark:border-gray-700 dark:text-gray-300"
          />
          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-600 border rounded dark:text-gray-400 dark:border-gray-700 dark:hover:bg-gray-600"
            >
              {t("discussion.report_modal.cancel")}
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-red-500 text-white rounded dark:bg-red-600 dark:text-white dark:hover:bg-red-700"
            >
              {t("discussion.report_modal.submit")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export const CommentActions = ({
  onEdit,
  onDelete,
  onReport,
  isOwner,
  user,
}) => {
  const { t } = useTranslation();
  return (
    <div className="text-xs text-gray-500 mt-0.5 flex items-center gap-2 dark:text-gray-400">
      {user && (
        <>
          <button
            onClick={onReport}
            className="text-red-200 hover:text-red-400 dark:text-red-400 dark:hover:text-red-600"
            title={t("discussion.report")}
          >
            <FontAwesomeIcon icon={faFlag} />
          </button>
          {isOwner && (
            <>
              <button
                onClick={onEdit}
                className="text-blue-500 hover:text-blue-600"
              >
                {t("discussion.edit")}
              </button>
              <button
                onClick={onDelete}
                className="text-red-500 hover:text-red-600"
              >
                {t("discussion.delete")}
              </button>
            </>
          )}
        </>
      )}
    </div>
  );
};

// PropTypes
ReplyForm.propTypes = {
  onSubmit: PropTypes.func.isRequired,
  text: PropTypes.string.isRequired,
  setText: PropTypes.func.isRequired,
  error: PropTypes.string,
  autoFocus: PropTypes.bool,
};

EditForm.propTypes = {
  text: PropTypes.string.isRequired,
  setText: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
};

ReportModal.propTypes = {
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  reason: PropTypes.string.isRequired,
  setReason: PropTypes.func.isRequired,
};

CommentActions.propTypes = {
  onEdit: PropTypes.func,
  onDelete: PropTypes.func,
  onReport: PropTypes.func,
  isOwner: PropTypes.bool,
  user: PropTypes.object,
};
